// =====================================================================
// REQUEST TEMPLATE TYPES
// Shared between frontend and backend
// =====================================================================

// Enums (match database enums)
export enum RequestType {
  FORM = "form",
  SIGNATURE = "signature",
}

export enum RoutingType {
  PARALLEL = "parallel",
  SEQUENTIAL = "sequential",
}

export enum TemplateType {
  REUSABLE = "reusable",
  ONE_OFF = "one_off",
}

export enum FieldType {
  TEXT = "text",
  EMAIL = "email",
  TEL = "tel",
  TEXTAREA = "textarea",
  SELECT = "select",
  CHECKBOX = "checkbox",
  RADIO = "radio",
  FILE = "file",
}

// Domain types
export type FormTemplateField = {
  id: string;
  template_id: string;
  key: string;
  label: string;
  field_type: FieldType;
  required: boolean;
  options?: Record<string, unknown> | string[]; // NO 'any'
  order: number;
  created_at: string;
};

export type SignatureTemplateSigner = {
  id: string;
  signature_template_id: string;
  key: string;
  label: string;
  signing_order?: number | null;
  required: boolean;
  created_at: string;
};

export type RequestTemplate = {
  id: string;
  type: RequestType;
  template_type: TemplateType;
  title: string;
  description: string | null;
  instructions: string | null;
  routing: RoutingType | null;
  signer_count: number;
  organization_id: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown>;
  // Relations (optional, for expanded queries)
  form_template_fields?: FormTemplateField[];
  signature_template_signers?: SignatureTemplateSigner[];
};

// DTOs
export type CreateFormTemplateFieldDto = Omit<
  FormTemplateField,
  "id" | "template_id" | "created_at"
>;

export type CreateSignatureTemplateSignerDto = Omit<
  SignatureTemplateSigner,
  "id" | "signature_template_id" | "created_at"
>;

export type CreateRequestTemplateDto = {
  type: RequestType;
  template_type?: TemplateType; // Defaults to 'reusable' if not provided
  title: string;
  description?: string;
  instructions?: string;
  routing?: RoutingType;
  organization_id: string;
  form_fields?: CreateFormTemplateFieldDto[];
  signature_signers?: CreateSignatureTemplateSignerDto[];
};

export type UpdateRequestTemplateDto = Partial<
  Omit<CreateRequestTemplateDto, "type" | "organization_id">
>;

// =====================================================================
// API RESPONSE TYPES
// Types that match what the backend API returns
// =====================================================================

/**
 * Signature place as returned from the database/API
 * Note: Uses page_number (not page) and includes foreign keys
 */
export type SignaturePlaceResponse = {
  id: string;
  signature_template_id: string;
  template_signer_id: string;
  pdf_file_id: string;
  type: "signature" | "initials" | "date" | "text";
  page_number: number;
  x: string; // Decimal comes as string from Prisma
  y: string;
  width: string;
  height: string;
  label: string | null;
  created_at: string | null;
  updated_at: string | null;
};

/**
 * PDF document as returned by getTemplateById for signature templates
 */
export type PdfDocumentResponse = {
  id: string;
  filename: string;
  original_filename: string;
  size_bytes: number;
  mime_type: string;
  created_at: string;
  signature_places: SignaturePlaceResponse[];
};

/**
 * Full response from getRequestTemplate API endpoint
 */
export type RequestTemplateResponse = RequestTemplate & {
  // Always included for expanded queries
  form_template_fields: FormTemplateField[];
  signature_template_signers: SignatureTemplateSigner[];
  // For signature templates only
  pdf_documents?: PdfDocumentResponse[];
};
